import { useCallback, useEffect, useState } from 'react'
import type { ApiError } from '../../lib/api'
import * as api from './api'
import { vt } from './strings'
import './verification.css'

/**
 * Owner-review skeleton. Lists residency checks that need a person (manual evidence, needs-more-information,
 * appeals) with the smallest amount of data: a display name, the kind of evidence and codes. It never shows
 * documents; the reviewer opens the provider's own dashboard for those. Reached at #/verify-review, and the
 * server answers 404 to anyone without the capability. Not linked from the navigation yet.
 */
export default function ReviewQueue() {
  const [rows, setRows] = useState<api.QueueRow[] | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  const refresh = useCallback(async () => {
    try { setRows((await api.getQueue()).queue) } catch (e) { setRows([]); if ((e as ApiError).status !== 404) setError(vt('error')) }
  }, [])
  useEffect(() => { void refresh() }, [refresh])
  async function decide(id: string, decision: 'verify' | 'decline' | 'ask_more') {
    setBusy(id); setError('')
    try { await api.decide(id, decision, decision === 'ask_more' ? 'more_evidence' : undefined); await refresh() } catch (e) { setError((e as ApiError).message || vt('error')) } finally { setBusy('') }
  }
  if (!rows) return <p role="status" className="market-empty">{vt('loading')}</p>
  return (
    <section className="v-page">
      <h1>{vt('reviewTitle')}</h1>
      {error && <p className="error" role="alert">{error}</p>}
      {!rows.length ? <p>{vt('reviewEmpty')}</p> : (
        <ul className="v-queue">
          {rows.map((r) => (
            <li key={r.id} className="v-card">
              <div className="v-card-head"><strong>{r.displayName}</strong><span className="v-chip" data-status={r.status}>{vt(r.status)}</span></div>
              <p>{vt(r.method === 'manual_evidence' ? 'reviewManual' : 'reviewId')}{r.appeal ? ` · ${vt('reviewAppeal')}` : ''}{r.synthetic ? ' · test' : ''}</p>
              <div className="v-actions">
                <button className="market-button coral" disabled={busy === r.id} onClick={() => void decide(r.id, 'verify')}>{vt('reviewVerify')}</button>
                <button className="market-button secondary" disabled={busy === r.id} onClick={() => void decide(r.id, 'ask_more')}>{vt('reviewMore')}</button>
                <button className="market-link" disabled={busy === r.id} onClick={() => void decide(r.id, 'decline')}>{vt('reviewDecline')}</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
